/**
 * ExpedienteHeader
 * Avatar con iniciales + nombre + badges + meta inline (teléfono, DIR, fecha de alta, wallet).
 * Versión compacta: una sola fila de cliente.
 */
import { Badge } from '@/components/ui/Badge';
import { CheckCircle2 } from 'lucide-react';
import { CreditLevelBadge } from '@/features/credit/components/CreditLevelBadge';
import { CopyableId } from '@/components/ui/CopyableId';
import { CreditLevel } from '@/types/credit.types';
import type { ExpedienteResumenResponse } from '@/types/expedientes.types';

interface ExpedienteHeaderProps {
  resumen: ExpedienteResumenResponse;
}

export function ExpedienteHeader({ resumen }: ExpedienteHeaderProps) {
  const initials = getInitials(resumen.full_name);
  const colorClass = getAvatarColor(resumen.directory_id);
  const clientSinceShort = new Date(resumen.client_since).toLocaleDateString('es-MX', {
    month: 'short',
    year: 'numeric',
  });

  return (
    <div className="flex items-center gap-3 border-b border-border px-4 py-3">
      <div
        className={`flex h-9 w-9 shrink-0 items-center justify-center rounded-full text-white font-semibold text-sm ${colorClass}`}
      >
        {initials}
      </div>
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 flex-wrap">
          <h1 className="text-base font-semibold truncate text-foreground">{resumen.full_name}</h1>
          {resumen.identity_verified.verified && (
            <Badge variant="success">
              <CheckCircle2 size={11} className="inline mr-1" />KYC verificado
            </Badge>
          )}
          {resumen.financial_profile?.credit_level &&
            Object.values(CreditLevel).includes(resumen.financial_profile.credit_level as CreditLevel) && (
            <CreditLevelBadge level={resumen.financial_profile.credit_level as CreditLevel} size="sm" />
          )}
        </div>
        <div className="mt-1 flex flex-wrap items-center gap-1.5 text-xs text-muted-foreground">
          <span>{resumen.phone || '—'}</span>
          <span>·</span>
          <CopyableId label="DIR" value={resumen.directory_id} />
          <span>·</span>
          <span>Cliente desde {clientSinceShort}</span>
          <span>·</span>
          <span>{resumen.wallet_source}</span>
        </div>
      </div>
    </div>
  );
}

function getInitials(fullName: string): string {
  if (!fullName) return '—';
  const parts = fullName.trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) return '—';
  if (parts.length === 1) return (parts[0] ?? '').slice(0, 2).toUpperCase();
  return ((parts[0]?.[0] ?? '') + (parts[1]?.[0] ?? '')).toUpperCase();
}

function getAvatarColor(seed: string): string {
  const palette = [
    'bg-blue-500', 'bg-purple-500', 'bg-pink-500', 'bg-orange-500',
    'bg-teal-500', 'bg-indigo-500', 'bg-cyan-600', 'bg-emerald-500',
  ];
  let hash = 0;
  for (let i = 0; i < seed.length; i++) hash = (hash * 31 + seed.charCodeAt(i)) >>> 0;
  return palette[hash % palette.length] ?? 'bg-gray-500';
}
